import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { MapPin, ArrowRight } from "lucide-react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.heat";
import { useSightingLocations } from "@/hooks/useSightingLocations";

declare module "leaflet" {
  function heatLayer(
    latlngs: [number, number, number][],
    options?: Record<string, unknown>
  ): L.Layer;
}

const HeatmapPreview = () => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstance = useRef<L.Map | null>(null);
  const { data: locations = [] } = useSightingLocations();

  useEffect(() => {
    if (!mapRef.current || mapInstance.current) return;

    const map = L.map(mapRef.current, {
      center: [57.28, -4.48],
      zoom: 10,
      zoomControl: false,
      scrollWheelZoom: false,
      dragging: false,
      doubleClickZoom: false,
      attributionControl: false,
    });
    mapInstance.current = map;

    L.tileLayer(
      "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
      { maxZoom: 19 }
    ).addTo(map);

    return () => {
      map.remove();
      mapInstance.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapInstance.current;
    if (!map || locations.length === 0) return;

    const addHeat = () => {
      const size = map.getSize();
      if (size.x === 0 || size.y === 0) return;
      const heatData: [number, number, number][] = locations.map((s) => [s.lat, s.lng, s.intensity]);
      L.heatLayer(heatData, {
        radius: 25,
        blur: 18,
        maxZoom: 13,
        max: 1.0,
        gradient: { 0.2: "#3b82f6", 0.4: "#06b6d4", 0.6: "#f59e0b", 0.8: "#f97316", 1.0: "#ef4444" },
      }).addTo(map);
    };

    setTimeout(() => {
      const size = map.getSize();
      if (size.x > 0 && size.y > 0) addHeat();
      else map.on("moveend", addHeat);
    }, 0);
  }, [locations]);

  return (
    <section className="py-12">
      <div className="flex items-end justify-between mb-6">
        <div>
          <p className="text-accent text-xs font-semibold tracking-[0.3em] uppercase mb-2">
            Interactive Map
          </p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-foreground">
            Sightings Heatmap
          </h2>
        </div>
        <Link
          to="/heatmap"
          className="hidden sm:flex items-center gap-2 text-sm text-accent hover:text-accent/80 transition-colors font-medium"
        >
          Explore full map <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
      <p className="text-muted-foreground mb-6 max-w-2xl">
        Over {locations.length} documented encounters mapped across the loch — from fleeting glimpses to multi-witness events spanning more than a century.
      </p>
      <div className="h-px bg-border mb-8" />

      <Link to="/heatmap" className="group block relative">
        <div
          ref={mapRef}
          className="w-full h-[350px] md:h-[450px] rounded-xl border border-border overflow-hidden"
        />
        {/* Overlay gradient + CTA */}
        <div className="absolute inset-0 rounded-xl bg-gradient-to-t from-background/80 via-transparent to-transparent pointer-events-none" />
        <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between pointer-events-none">
          <div className="flex items-center gap-2 text-muted-foreground text-sm">
            <MapPin className="h-4 w-4 text-accent" />
            <span>{locations.length} sightings documented</span>
          </div>
          <span className="flex items-center gap-2 text-accent font-medium text-sm group-hover:underline">
            Open interactive map <ArrowRight className="h-4 w-4" />
          </span>
        </div>
      </Link>

      <Link
        to="/heatmap"
        className="sm:hidden flex items-center justify-center gap-2 mt-4 text-sm text-accent hover:text-accent/80 transition-colors font-medium"
      >
        Explore full map <ArrowRight className="h-4 w-4" />
      </Link>
    </section>
  );
};

export default HeatmapPreview;
